import { STAGE } from '../../design/lab/palette';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import BlobBot from './BlobBot';
import { BotState } from './motionSystem';
import { useReducedMotion } from '../../animations';

export interface MotionPlayerProps {
    /** Order to tour. Defaults to the agent lifecycle. */
    states?: BotState[];
    /** Seconds each state is held */
    dwell?: number;
    size?: number;
    color?: string;
    /** Start paused (reduced motion always starts paused) */
    autoplay?: boolean;
}

const DEFAULT_TOUR: BotState[] = ['idle', 'curious', 'thinking', 'working', 'waiting', 'blocked', 'done'];

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

/**
 * MotionPlayer — a lifecycle tour for an avatar.
 *
 * One avatar, one state at a time, with a progress bar per state so the tour is
 * scrubbable and its position legible. Motion is only honestly documentable by
 * running it: a still frame of "waiting" and a still frame of "blocked" look
 * nearly identical, and the whole point is that they do not feel identical.
 *
 * Knows nothing about chat or agents — give it a list of states and it tours
 * them.
 */
const MotionPlayer: React.FC<MotionPlayerProps> = ({
    states = DEFAULT_TOUR,
    dwell = 2.6,
    size = 108,
    color = STAGE.violet,
    autoplay = true,
}) => {
    const reduced = useReducedMotion();
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(autoplay && !reduced);
    const [progress, setProgress] = useState(0);
    const rafRef = useRef(0);
    const startRef = useRef(0);

    useEffect(() => {
        if (!playing) return;
        startRef.current = performance.now();
        const tick = (now: number) => {
            const p = Math.min(1, (now - startRef.current) / (dwell * 1000));
            setProgress(p);
            if (p >= 1) {
                startRef.current = now;
                setIndex(i => (i + 1) % states.length);
                setProgress(0);
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [playing, dwell, states.length]);

    const select = useCallback((i: number) => {
        setIndex(i);
        setProgress(0);
        startRef.current = performance.now();
    }, []);

    const current = states[index];

    return (
        <div style={{
            position: 'relative',
            padding: '18px 14px 14px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: '12px',
            margin: '10px 0 14px',
        }}>
            <button
                type="button" aria-pressed={playing}
                onClick={() => setPlaying(p => !p)}
                aria-label={playing ? 'Pause the lifecycle tour' : 'Play the lifecycle tour'}
                style={{
                    position: 'absolute', top: '10px', right: '10px',
                    width: 44, height: 44, borderRadius: '50%',
                    display: 'grid', placeItems: 'center',
                    background: 'rgba(255,255,255,0.07)', border: 'none',
                    color: STAGE.muted, cursor: 'pointer', fontSize: '10px',
                }}
            >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    {playing ? <><path d="M5 3v10M11 3v10" /></> : <path d="M5 3l8 5-8 5V3z" />}
                </svg>
            </button>

            <div style={{ display: 'grid', placeItems: 'center', minHeight: size + 24 }}>
                <BlobBot color={color} size={size} state={current} />
            </div>

            {/* label row with per-state progress — their scrubber pattern */}
            <div style={{
                display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
                gap: '4px 8px',
                marginTop: '18px',
            }}>
                {states.map((st, i) => {
                    const active = i === index;
                    const done = i < index;
                    return (
                        <button
                            key={st} type="button" aria-pressed={active} aria-label={`Show ${st} state`}
                            onClick={() => select(i)}
                            style={{
                                background: 'transparent', border: 'none', padding: 0, minHeight: 44,
                                flex: '1 0 60px',
                                cursor: 'pointer', textAlign: 'center',
                            }}
                        >
                            <div style={{
                                ...mono, fontSize: '10.5px',
                                color: active ? STAGE.ink : STAGE.muted,
                                fontWeight: active ? 700 : 400,
                                transition: 'color 0.3s',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{st}</div>
                            <div style={{
                                height: 2, marginTop: 7, borderRadius: 2,
                                background: 'rgba(255,255,255,0.10)',
                                overflow: 'hidden',
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${active ? progress * 100 : done ? 100 : 0}%`,
                                    background: active ? color : 'rgba(255,255,255,0.22)',
                                    transformOrigin: '0 50%',
                                }} />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default MotionPlayer;
